import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Envia o httpOnly cookie de sessão (`flua_session`) em todas as chamadas ao API.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch (err) {
      // 401 esperado quando não logado; log apenas o resto.
      if (err?.response?.status !== 401) {
        console.error("Sessão inválida ao restaurar:", err);
      }
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    // Backend seta o cookie httpOnly; não guardamos o token no navegador.
    await axios.post(`${API}/auth/login`, { email, password });
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      return me.data;
    } catch (err) {
      console.error("Falha ao carregar /auth/me após login:", err);
      throw err;
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); }
    catch (err) { console.error("Falha ao encerrar sessão remota:", err); }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const isAdmin = (user) => user?.role === "admin";
export const isTecnico = (user) => user?.role === "tecnico";
export const isVendedora = (user) => user?.role === "vendedora";

/** Verifica se o usuário logado possui uma permissão específica.
 *  Admin sempre retorna true. Se `perm` for null/undefined, retorna true. */
export const hasPermission = (user, perm) => {
  if (!user) return false;
  if (!perm) return true;
  if (user.role === "admin") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
};
